import React from 'react';
import { Code2, ChevronRight } from 'lucide-react';

interface Technology {
  id: string;
  name: string;
  category: string;
  count: number;
  percentage: number;
  trend: 'up' | 'down' | 'stable';
  color: string;
  icon?: string;
}

interface TopTechnologiesProps {
  technologies: Technology[];
}

const TopTechnologies: React.FC<TopTechnologiesProps> = ({ technologies }) => {
  const getTrendColor = (trend: string) => {
    switch (trend) {
      case 'up': return 'text-emerald-500';
      case 'down': return 'text-red-500';
      default: return 'text-gray-500';
    }
  };

  return (
    <div className="bg-[#0F1629] rounded-xl p-6 relative overflow-hidden group">
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Code2 className="h-5 w-5 text-indigo-500" />
            <h2 className="text-lg font-semibold text-white">Top Technologies</h2>
          </div>
        </div>

        {/* Technologies List */}
        <div className="space-y-4">
          {technologies.map((tech) => (
            <div
              key={tech.id}
              className="group/tech bg-gray-800/20 rounded-lg p-4 hover:bg-gray-800/30 transition-all duration-200 cursor-pointer"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg bg-gray-800/30 ${tech.color}`}>
                    {tech.icon ? (
                      <img
                        src={`https://www.wappalyzer.com/images/icons/${tech.icon}`}
                        alt={tech.name}
                        className="h-4 w-4"
                      />
                    ) : (
                      <Code2 className="h-4 w-4" />
                    )}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-300">
                      {tech.name}
                    </div>
                    <div className="text-xs text-gray-500">
                      {tech.category}
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-400">
                      {tech.count} assets
                    </div>
                    <div className={`text-xs ${getTrendColor(tech.trend)}`}>
                      {tech.percentage}%
                    </div>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-500 opacity-0 group-hover/tech:opacity-100 transition-opacity" />
                </div>
              </div>
              <div className="mt-3">
                <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${tech.color} transition-all duration-500`}
                    style={{ width: `${tech.percentage}%` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TopTechnologies;